import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, MapPin, Calendar, Building } from "lucide-react";
import { thumb } from "@/lib/img";
import SeoCopyBlock from "@/components/SeoCopyBlock";
import { SEO_COPY } from "@/data/seoCopy";
import useSEO from "@/hooks/useSEO";
import QuickQuote from "@/components/QuickQuote";
import {
  getRegionBySlug,
  getCitiesByRegion,
  getExposByRegion,
  getCountriesByRegion,
  REGIONAL_CONTACTS,
} from "@/data/clientsData";
import {
  breadcrumbSchema,
  itemListSchema,
  organizationSchema,
} from "@/lib/seo-schemas";
import LatestHandoverPopup from "@/components/LatestHandoverPopup";

const RegionPage = () => {
  const { slug } = useParams();
  const region = getRegionBySlug(slug);
  const cities = getCitiesByRegion(slug);
  const regionExpos = getExposByRegion(slug);
  const regionCountries = getCountriesByRegion(slug);

  useSEO({
    title: region
      ? SEO_COPY[`region-${region.slug}`]?.metaTitle || `Exhibition Stand & Booth Builders in ${region.name} | Stagehook`
      : "Region not found",
    description: region
      ? SEO_COPY[`region-${region.slug}`]?.metaDescription || `Stagehook Global is a leading exhibition stall designer & expo booth builder across ${region.name}. Turnkey stand design, fabrication & installation in ${cities
          .map((c) => c.city)
          .slice(0, 5)
          .join(", ")} and more.`
      : "",
    keywords: region
      ? `expo booth builder ${region.name}, exhibition stall designer ${region.name}, trade fair stand ${region.name}, ${cities
          .map((c) => `${c.city} booth builder`)
          .join(", ")}`
      : "",
    image: region?.heroImage,
    path: `/region/${slug}`,
    jsonLd: region
      ? [
          organizationSchema(),
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: region.name, path: `/region/${region.slug}` },
          ]),
          itemListSchema(
            `Expos in ${region.name}`,
            regionExpos.map((e) => ({
              name: `${e.name} — ${e.city} stand builder`,
              path: `/expo/${e.slug}`,
            }))
          ),
        ]
      : null,
  });

  if (!region) return <Navigate to="/404" replace />;

  const latestHandover = regionExpos.find((e) => e.latest);

  return (
    <main data-testid="region-page" className="pt-[72px]">
      {/* HERO */}
      <section className="relative min-h-[60vh] flex items-end overflow-hidden grain" style={{ background: "var(--bg)" }}>

        <div className="shg-container w-full relative z-10 py-20">
          <Link
            to="/"
            data-testid="back-home"
            className="inline-flex items-center gap-2 text-sm text-[var(--text-soft)] hover:text-[var(--red)] mb-8 transition-colors"
          >
            <ArrowLeft size={16} /> Back to home
          </Link>
          <div className="flex items-center gap-4 mb-6">
            <span className="text-5xl">{region.flag}</span>
            <div className="font-mono text-xs tracking-[0.25em] text-[var(--red)]">
              EXPO BOOTH BUILDERS · {region.shortName.toUpperCase()}
            </div>
          </div>
          <h1 className="font-display text-6xl md:text-8xl leading-[0.9] mb-6 max-w-4xl">
            Exhibition stalls in<br />
            <span style={{ color: "var(--red)" }}>{region.name}</span>.
          </h1>
          <p className="text-lg md:text-xl text-[var(--text-soft)] max-w-2xl">
            {region.intro}
          </p>

          <div className="flex flex-wrap gap-3 mt-8">
            <span className="flag-chip"><Calendar size={12} /> {regionExpos.length} Expos</span>
            <span className="flag-chip"><MapPin size={12} /> {cities.length} Cities</span>
            <span className="flag-chip"><Building size={12} /> {regionCountries.length} {regionCountries.length === 1 ? "Country" : "Countries"}</span>
          </div>
        </div>
      </section>

      {/* CITY-WISE EXPOS */}
      <section data-testid="cities-section" className="py-24">
        <div className="shg-container">
          <div className="mb-14 max-w-3xl">
            <div className="section-tag mb-4"><span className="dot" />City-wise breakdown</div>
            <h2 className="font-display text-5xl leading-[0.95]">
              Cities we build in.<br />
              <span style={{ color: "var(--red)" }}>Expos we build at.</span>
            </h2>
          </div>

          <div className="space-y-16">
            {cities.map((cityBlock, i) => (
              <div key={`${cityBlock.city}-${i}`} data-testid={`city-block-${cityBlock.city.toLowerCase()}`}>
                {/* City header */}
                <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8 pb-5 border-b" style={{ borderColor: "var(--line)" }}>
                  <div>
                    <div className="font-mono text-[10px] tracking-[0.25em] text-[var(--text-muted)] mb-2">
                      {cityBlock.countryName.toUpperCase()} · {cityBlock.expos.length} EXPOS
                    </div>
                    <h3 className="font-display text-5xl md:text-6xl">
                      {cityBlock.city}<span style={{ color: "var(--red)" }}>.</span>
                    </h3>
                  </div>
                  <div className="font-mono text-xs text-[var(--text-muted)] tracking-[0.15em]">
                    {cityBlock.expos.reduce((s, e) => s + e.clients.length, 0)} STANDS BUILT
                  </div>
                </div>

                {/* Expos in city */}
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
                  {cityBlock.expos.map((expo) => (
                    <Link
                      key={expo.slug}
                      to={`/expo/${expo.slug}`}
                      data-testid={`region-expo-${expo.slug}`}
                      className="shg-card group block"
                    >
                      <div className="aspect-[16/10] overflow-hidden relative">
                        <img
                          src={thumb(expo.heroImage)}
                          alt={`${expo.name} stand builder in ${expo.city}`}
                          loading="lazy"
                          decoding="async"
                          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                        />
                        {expo.latest && (
                          <div
                            data-testid={`latest-chip-${expo.slug}`}
                            className="absolute top-4 left-4 inline-flex items-center gap-1.5 font-mono text-[10px] tracking-[0.2em] px-2.5 py-1 rounded-full text-white"
                            style={{ background: "var(--red)" }}
                          >
                            <span className="relative flex h-1.5 w-1.5">
                              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" />
                              <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-white" />
                            </span>
                            LATEST
                          </div>
                        )}
                        <div className="absolute top-4 right-4 flag-chip">
                          <Calendar size={11} /> {expo.month}
                        </div>
                      </div>
                      <div className="p-6">
                        <h4 className="font-display text-2xl mb-2 group-hover:text-[var(--red)] transition-colors">
                          {expo.name}
                        </h4>
                        <p className="text-sm text-[var(--text-soft)] line-clamp-2 mb-3">
                          {expo.description}
                        </p>
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-[var(--text-muted)] font-mono">
                            {expo.clients.length} {expo.clients.length === 1 ? "STAND" : "STANDS"} · {expo.venue.split(",")[0]}
                          </span>
                          <ArrowUpRight size={15} className="text-[var(--red)]" />
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <SeoCopyBlock id={`region-${region.slug}`} />

      {/* CTA */}
      <section className="pb-24">
        <div className="shg-container">
          <div
            className="rounded-2xl p-10 md:p-14 flex flex-col md:flex-row md:items-center justify-between gap-6"
            style={{ background: "var(--surface)", border: "1px solid var(--line-strong)" }}
          >
            <div>
              <div className="shg-eyebrow mb-3">Exhibiting in {region.name}?</div>
              <h3 className="font-display text-3xl md:text-4xl mb-4">
                Get your free concept &amp; quote.
              </h3>
              {(() => {
                const contact = REGIONAL_CONTACTS.find(
                  (c) => c.region.toLowerCase() === region.shortName.toLowerCase()
                );
                if (!contact) return null;
                return (
                  <a
                    href={`tel:${contact.dial}`}
                    data-testid={`region-phone-${region.slug}`}
                    className="inline-flex items-center gap-3 text-lg font-medium hover:text-[var(--red)] transition-colors"
                  >
                    <span className="text-xl">{contact.flag}</span>
                    <span className="font-mono text-xs tracking-[0.2em] text-[var(--text-muted)]">
                      DIRECT LINE:
                    </span>
                    <span className="font-display text-2xl">{contact.phone}</span>
                  </a>
                );
              })()}
            </div>
            <Link to="/contact" className="btn-primary self-start">
              Talk to our team <ArrowUpRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      <section data-testid="region-quote-section" className="pb-20">
        <div className="shg-container max-w-3xl"><QuickQuote expoName={`an expo in ${region.name}`} source="region" /></div>
      </section>

      {latestHandover && <LatestHandoverPopup expo={latestHandover} />}
    </main>
  );
};

export default RegionPage;
